import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

interface UseInstallPromptReturn {
  isInstallable: boolean;
  isIOS: boolean;
  isStandalone: boolean;
  promptInstall: () => Promise<boolean>;
  dismissPrompt: () => void;
  resetDismissal: () => void;
  showIOSInstructions: boolean;
  setShowIOSInstructions: (show: boolean) => void;
}

export function useInstallPrompt(): UseInstallPromptReturn {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem('pwa-install-dismissed') === 'true';
  });

  // Detect iOS
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as unknown as { MSStream?: unknown }).MSStream;

  // Detect if running as standalone (already installed)
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true;

  useEffect(() => {
    // Don't show install prompt if already installed or dismissed
    if (isStandalone || isDismissed) {
      return;
    }

    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsInstallable(false);
      console.log('PWA was installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isStandalone, isDismissed]);

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) {
      // On iOS, show manual instructions
      if (isIOS && !isStandalone) {
        setShowIOSInstructions(true);
        return false;
      }
      return false;
    }

    // Show the install prompt
    await deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const choiceResult = await deferredPrompt.userChoice;

    // Clear the deferred prompt
    setDeferredPrompt(null);
    setIsInstallable(false);

    return choiceResult.outcome === 'accepted';
  }, [deferredPrompt, isIOS, isStandalone]);

  const dismissPrompt = useCallback(() => {
    setIsInstallable(false);
    setShowIOSInstructions(false);
    setIsDismissed(true);
    // Store dismissal in localStorage to persist across sessions
    localStorage.setItem('pwa-install-dismissed', 'true');
  }, []);

  const resetDismissal = useCallback(() => {
    localStorage.removeItem('pwa-install-dismissed');
    setIsDismissed(false);
    // For iOS, immediately show instructions
    if (isIOS && !isStandalone) {
      setShowIOSInstructions(true);
    }
  }, [isIOS, isStandalone]);

  // Calculate if prompt should be shown (respects dismissal for all platforms)
  const shouldShowPrompt = !isDismissed && !isStandalone && (isInstallable || isIOS);

  return {
    isInstallable: shouldShowPrompt,
    isIOS,
    isStandalone,
    promptInstall,
    dismissPrompt,
    resetDismissal,
    showIOSInstructions,
    setShowIOSInstructions,
  };
}
